import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
	Card,
	CircularProgress,
	Grid,
	IconButton,
	styled,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from "@mui/material";
import Refresh from "@mui/icons-material/Refresh";

import { type Classification } from "@/schemas";

import { ClassificationEditButton } from "../components/classification-edit-button";
import { ClassificationDeleteButton } from "../components/classification-delete-button";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	"&:nth-of-type(even)": {
		backgroundColor: theme.palette.action.hover,
	},
}));

export interface ClassificationsTemplateProps {
	readonly classifications: Classification[];
	readonly isLoading: boolean;
	readonly isError: boolean;
	readonly onRetry: () => void;
}

export const ClassificationsTemplate: React.FC<ClassificationsTemplateProps> = ({
	classifications,
	isLoading,
	isError,
	onRetry,
}) => {
	const intl = useIntl();
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(50);

	return (
		<Card elevation={0} sx={{ display: "flex", flexDirection: "column" }}>
			<TableContainer sx={{ flex: 1 }}>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell>
								<FormattedMessage id="Alias" defaultMessage="Alias" />
							</TableCell>
							<TableCell>
								<FormattedMessage id="Number" defaultMessage="Number" />
							</TableCell>
							<TableCell>
								<FormattedMessage id="Is Priority Label" defaultMessage="Is Priority Label" />
							</TableCell>
							<TableCell>
								<FormattedMessage id="Status" defaultMessage="Status" />
							</TableCell>
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{isLoading === true && (
							<StyledTableRow sx={{ height: 100 }}>
								<TableCell colSpan={1000} align="center" sx={{ borderBottom: "none" }}>
									<CircularProgress color="info" data-testid="classifications__progress__loading" />
								</TableCell>
							</StyledTableRow>
						)}
						{isError === true && isLoading === false && (
							<StyledTableRow sx={{ height: 100 }}>
								<TableCell colSpan={1000} align="center" sx={{ borderBottom: "none" }}>
									<Grid container justifyContent="center" alignItems="center" gap={2}>
										<Typography component="span" color="error" data-testid="classifications__text__error">
											<FormattedMessage
												id="Failed to load classifications. Please try again."
												defaultMessage="Failed to load classifications. Please try again."
											/>
										</Typography>
										<IconButton onClick={onRetry} data-testid="classifications__button__retry">
											<Refresh />
										</IconButton>
									</Grid>
								</TableCell>
							</StyledTableRow>
						)}
						{isLoading === false && isError === false && classifications.length === 0 && (
							<StyledTableRow>
								<TableCell colSpan={1000} align="center">
									<Typography color="textDisabled" data-testid="classifications__text__no-classifications">
										<FormattedMessage
											id="No Classifications Configured"
											defaultMessage="No Classifications Configured"
										/>
									</Typography>
								</TableCell>
							</StyledTableRow>
						)}
						{isLoading === false &&
							isError === false &&
							classifications.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((classification) => (
								<StyledTableRow key={classification.id}>
									<TableCell sx={{ fontWeight: 500 }}>{classification.alias}</TableCell>
									<TableCell>{classification.number}</TableCell>
									<TableCell>
										{classification.isPriorityLabel
											? intl.formatMessage({ id: "Yes", defaultMessage: "Yes" })
											: intl.formatMessage({ id: "No", defaultMessage: "No" })}
									</TableCell>
									<TableCell>{classification.status}</TableCell>
									<TableCell>
										<Grid container gap={1} justifyContent="flex-end">
											<ClassificationEditButton classification={classification} />
											<ClassificationDeleteButton classification={classification} />
										</Grid>
									</TableCell>
								</StyledTableRow>
							))}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				rowsPerPageOptions={[50, 100, 500]}
				component="div"
				count={classifications.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={(_, pageNumber) => setPage(pageNumber)}
				onRowsPerPageChange={(event) => {
					setRowsPerPage(parseInt(event.target.value));
					setPage(0);
				}}
			/>
		</Card>
	);
};
